/**
 * Combat / UI sound effects, synthesised in real time via Web Audio API.
 *
 * Each preset is a short envelope around an oscillator (or filtered noise),
 * 80-300 ms in length. No sample assets to ship — works offline, and the
 * sound design is consistent across browsers.
 *
 * Browsers gate AudioContext creation behind a user gesture, so we lazy-
 * init on first play attempt. If the context never resumes (page never
 * focused), play() is a no-op.
 */

export type SfxName =
  | 'swing'        // sword/melee swing
  | 'hit'          // damage taken / dealt
  | 'crit'         // critical hit
  | 'miss'         // miss
  | 'cast'         // generic spell cast
  | 'heal'         // heal sparkle
  | 'block'        // shield block
  | 'bow-draw'     // bow string draw
  | 'arrow-loose'  // arrow release
  | 'levelup'      // level-up jingle
  | 'click';       // ui click

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let muted = false;

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctor = W.AudioContext ?? W.webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.4;
    masterGain.connect(ctx.destination);
  } catch {
    return null;
  }
  return ctx;
}

function noiseBuffer(c: AudioContext, durationSec: number): AudioBuffer {
  const len = Math.floor(c.sampleRate * durationSec);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

function envOsc(freqStart: number, freqEnd: number, durationSec: number, type: OscillatorType, vol = 0.35): void {
  const c = ensureCtx();
  if (!c || !masterGain || muted) return;
  if (c.state === 'suspended') void c.resume();
  const now = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freqStart, now);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, freqEnd), now + durationSec);
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol, now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.001, now + durationSec);
  osc.connect(g);
  g.connect(masterGain);
  osc.start(now);
  osc.stop(now + durationSec + 0.02);
}

function envNoise(durationSec: number, filterFreq: number, q: number, vol = 0.4): void {
  const c = ensureCtx();
  if (!c || !masterGain || muted) return;
  if (c.state === 'suspended') void c.resume();
  const now = c.currentTime;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, durationSec);
  const filt = c.createBiquadFilter();
  filt.type = 'bandpass';
  filt.frequency.value = filterFreq;
  filt.Q.value = q;
  const g = c.createGain();
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol, now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.001, now + durationSec);
  src.connect(filt);
  filt.connect(g);
  g.connect(masterGain);
  src.start(now);
  src.stop(now + durationSec + 0.02);
}

function chord(freqs: number[], durationSec: number, type: OscillatorType, vol = 0.2): void {
  for (const f of freqs) envOsc(f, f, durationSec, type, vol);
}

export function playSfx(name: SfxName): void {
  switch (name) {
    case 'swing':
      // High → low whoosh.
      envNoise(0.18, 1500, 4, 0.35);
      envOsc(900, 200, 0.18, 'sawtooth', 0.12);
      break;
    case 'hit':
      // Punchy thud.
      envOsc(180, 60, 0.12, 'square', 0.32);
      envNoise(0.08, 400, 2, 0.25);
      break;
    case 'crit':
      // Hit + bright sparkle.
      envOsc(180, 60, 0.12, 'square', 0.4);
      envNoise(0.08, 600, 2, 0.3);
      envOsc(1800, 1200, 0.18, 'triangle', 0.18);
      break;
    case 'miss':
      // Soft puff.
      envNoise(0.16, 800, 1, 0.18);
      break;
    case 'cast':
      // Rising magical chirp.
      envOsc(220, 880, 0.28, 'triangle', 0.22);
      envOsc(440, 1320, 0.28, 'sine', 0.14);
      break;
    case 'heal':
      // Major triad sparkle.
      chord([523, 659, 784], 0.4, 'sine', 0.16);
      break;
    case 'block':
      // Metallic clank — narrow noise + low thud.
      envNoise(0.12, 2200, 16, 0.3);
      envOsc(140, 80, 0.12, 'square', 0.18);
      break;
    case 'bow-draw':
      envNoise(0.32, 200, 8, 0.18);
      break;
    case 'arrow-loose':
      envNoise(0.12, 3500, 12, 0.28);
      break;
    case 'levelup':
      // Ascending arpeggio.
      setTimeout(() => envOsc(523, 523, 0.15, 'triangle', 0.25), 0);
      setTimeout(() => envOsc(659, 659, 0.15, 'triangle', 0.25), 100);
      setTimeout(() => envOsc(784, 784, 0.15, 'triangle', 0.25), 200);
      setTimeout(() => envOsc(1047, 1047, 0.3, 'triangle', 0.3), 300);
      break;
    case 'click':
      envOsc(720, 660, 0.06, 'square', 0.18);
      break;
  }
}

export function setSfxMuted(m: boolean): void {
  muted = m;
}

export function isSfxMuted(): boolean {
  return muted;
}
